import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ShoppingBag, Heart } from 'lucide-react';
import useStore from '../store/useStore';
import { translations } from '../i18n/translations';
import { addToWishlist, removeFromWishlist } from '../api/api';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

// Helper to resolve image URLs (handles both local uploads and external URLs)
const resolveImageUrl = (url) => {
  if (!url) return 'https://via.placeholder.com/400x500?text=No+Image';
  // If it's a relative URL starting with /api/, prefix with backend URL
  if (url.startsWith('/api/')) {
    return `${BACKEND_URL}${url}`;
  }
  // If it's already an absolute URL, use it as-is
  return url;
};

const ProductCard = ({ product }) => {
  const { language, addToCart, isAuthenticated, wishlist, addToWishlist: addToWishlistLocal, removeFromWishlistLocal } = useStore();
  const t = translations[language];
  const [wishlistLoading, setWishlistLoading] = useState(false);

  const translation = product.translations?.[language] || product.translations?.fr || {};
  // Fix: original_price doit être > 0 ET > price pour afficher la promo
  const hasDiscount = product.original_price && product.original_price > 0 && product.original_price > product.price;
  const discount = hasDiscount
    ? Math.round((1 - product.price / product.original_price) * 100)
    : 0;

  const isInWishlist = wishlist.includes(product.id);

  // Resolve image URL
  const imageUrl = resolveImageUrl(product.images?.[0]);

  const handleQuickAdd = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Get first available variant
    const availableVariant = product.variants?.find((v) => v.stock > 0);
    if (availableVariant) {
      addToCart({
        product_id: product.id,
        size: availableVariant.size,
        color: availableVariant.color,
        quantity: 1,
        price: product.price,
        name: translation.name,
        image: imageUrl,
      });
    }
  };

  const handleWishlistToggle = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!isAuthenticated) {
      // Could trigger auth modal here
      return;
    }

    setWishlistLoading(true);
    try {
      if (isInWishlist) {
        await removeFromWishlist(product.id);
        removeFromWishlistLocal(product.id);
      } else {
        await addToWishlist(product.id);
        addToWishlistLocal(product.id);
      }
    } catch (error) {
      console.error('Wishlist error:', error);
    } finally {
      setWishlistLoading(false);
    }
  };

  const isInStock = product.variants?.some((v) => v.stock > 0);

  return (
    <Link
      to={`/product/${product.id}`}
      className="group bg-white rounded-lg overflow-hidden shadow-md hover:shadow-xl transition-all duration-300"
      data-testid={`product-card-${product.id}`}
    >
      {/* Image Container */}
      <div className="relative aspect-[3/4] overflow-hidden bg-gray-100">
        <img
          src={imageUrl}
          alt={translation.name}
          loading="lazy"
          decoding="async"
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
        />
        
        {/* Badges */}
        <div className="absolute top-3 left-3 flex flex-col gap-2">
          {hasDiscount && (
            <span className="bg-red-500 text-white text-xs font-bold px-2 py-1 rounded">
              -{discount}%
            </span>
          )}
          {product.new_arrival && (
            <span className="bg-green-600 text-white text-xs font-bold px-2 py-1 rounded">
              {t.products.new}
            </span>
          )}
        </div>

        {/* Wishlist Button */}
        {isAuthenticated && (
          <button
            onClick={handleWishlistToggle}
            disabled={wishlistLoading}
            className={`absolute top-3 right-3 p-2 rounded-full transition-all duration-300 ${
              isInWishlist
                ? 'bg-red-500 text-white'
                : 'bg-white/80 text-gray-600 hover:bg-white hover:text-red-500'
            }`}
            title={isInWishlist ? t.wishlist.removedFromWishlist : t.wishlist.addedToWishlist}
          >
            <Heart className={`w-5 h-5 ${isInWishlist ? 'fill-current' : ''}`} />
          </button>
        )}

        {/* Quick Add Button */}
        {isInStock && (
          <button
            onClick={handleQuickAdd}
            className="absolute bottom-3 right-3 bg-amber-700 hover:bg-amber-800 text-white p-3 rounded-full opacity-0 group-hover:opacity-100 transition-all duration-300 transform translate-y-2 group-hover:translate-y-0"
            title={t.products.addToCart}
          >
            <ShoppingBag className="w-5 h-5" />
          </button>
        )}

        {/* Out of Stock Overlay */}
        {!isInStock && (
          <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
            <span className="bg-white text-gray-900 px-4 py-2 rounded font-medium">
              {t.products.outOfStock}
            </span>
          </div>
        )}
      </div>

      {/* Info */}
      <div className="p-4">
        <h3 className="font-medium text-gray-900 group-hover:text-amber-700 transition-colors line-clamp-1">
          {translation.name}
        </h3>
        <p className="text-sm text-gray-500 mt-1 line-clamp-2">
          {translation.description?.substring(0, 60)}...
        </p>
        <div className="mt-3 flex items-center gap-2">
          <span className="text-lg font-bold text-amber-900">
            €{product.price?.toFixed(2)}
          </span>
          {hasDiscount && (
            <span className="text-sm text-gray-400 line-through">
              €{product.original_price?.toFixed(2)}
            </span>
          )}
        </div>

        {/* Color Swatches */}
        <div className="mt-3 flex gap-1">
          {[...new Set(product.variants?.map((v) => v.color_hex))].slice(0, 4).map((hex, idx) => (
            <div
              key={idx}
              className="w-4 h-4 rounded-full border border-gray-300"
              style={{ backgroundColor: hex }}
              title={product.variants?.find((v) => v.color_hex === hex)?.color}
            />
          ))}
        </div>
      </div>
    </Link>
  );
};

export default ProductCard;
